import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Boxes, LogIn } from "lucide-react";
import { toast } from "sonner";

const BANNER = "https://images.pexels.com/photos/7513430/pexels-photo-7513430.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [fEmail, setFEmail] = useState("");
  const [resetToken, setResetToken] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email, password);
      nav("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const forgot = async () => {
    try {
      const { data } = await api.post("/auth/forgot-password", { email: fEmail });
      if (data.reset_token) {
        setResetToken(data.reset_token);
        toast.success("Lien de réinitialisation généré.");
      } else {
        toast.success(data.message || "Demande envoyée.");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-900">
      <div className="relative hidden lg:block">
        <img src={BANNER} alt="Entrepôt logistique" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-slate-950/75" />
        <div className="relative z-10 h-full flex flex-col justify-end p-12 text-white">
          <div className="h-14 w-14 rounded-xl bg-blue-600 flex items-center justify-center mb-6">
            <Boxes className="h-8 w-8" />
          </div>
          <h1 className="font-display font-extrabold text-4xl leading-tight uppercase tracking-tight">
            Suivi logistique<br />des emballages
          </h1>
          <p className="mt-4 text-slate-300 max-w-md">
            Mouvements d'emballages sales et propres entre ANNEZIN, DDO et les sites destinataires. Stocks temps réel, réception contradictoire et alertes de seuil.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 bg-background">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="h-11 w-11 rounded-xl bg-blue-700 flex items-center justify-center">
              <Boxes className="h-6 w-6 text-white" />
            </div>
            <span className="font-display font-extrabold text-xl uppercase tracking-tight">Suivi Emballages</span>
          </div>
          <h2 className="font-display font-bold text-2xl text-slate-900 mb-1">Connexion</h2>
          <p className="text-slate-500 mb-6 text-sm">Accédez à votre espace de suivi.</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                className="h-12" required data-testid="login-email-input" />
            </div>
            <div>
              <Label htmlFor="password">Mot de passe</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                className="h-12" required data-testid="login-password-input" />
            </div>
            {error && <p className="text-sm text-red-600 font-medium" data-testid="login-error">{error}</p>}
            <Button type="submit" disabled={busy}
              className="w-full h-13 h-14 bg-blue-800 hover:bg-blue-900 text-white font-bold text-base tactile"
              data-testid="login-submit-button">
              <LogIn className="h-5 w-5 mr-2" />
              {busy ? "Connexion…" : "Se connecter"}
            </Button>
            <button type="button" onClick={() => setForgotOpen(true)}
              className="w-full text-center text-sm text-blue-700 font-semibold hover:underline"
              data-testid="forgot-password-link">
              Mot de passe oublié ?
            </button>
          </form>

        </div>
      </div>

      <Dialog open={forgotOpen} onOpenChange={(o) => { setForgotOpen(o); if (!o) setResetToken(""); }}>
        <DialogContent data-testid="forgot-dialog">
          <DialogHeader>
            <DialogTitle>Mot de passe oublié</DialogTitle>
            <DialogDescription>
              Saisissez votre email. Un lien de réinitialisation sera généré (outil interne, sans envoi d'email externe).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input type="email" placeholder="votre@email.fr" value={fEmail}
              onChange={(e) => setFEmail(e.target.value)} className="h-12" data-testid="forgot-email-input" />
            {resetToken && (
              <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm">
                <p className="text-green-800 font-semibold mb-2">Lien généré. Cliquez pour définir un nouveau mot de passe :</p>
                <Button className="w-full bg-green-600 hover:bg-green-700 text-white"
                  onClick={() => { setForgotOpen(false); nav(`/reset-password?token=${resetToken}`); }}
                  data-testid="goto-reset-button">
                  Réinitialiser maintenant
                </Button>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setForgotOpen(false)}>Fermer</Button>
            <Button onClick={forgot} className="bg-blue-800 hover:bg-blue-900" data-testid="forgot-submit-button">
              Générer le lien
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
